import _ from 'underscore';
import Backbone from 'backbone';

// utils -- userSocialProfileImage
import profileImageHelperV2 from 'utils/userSocialProfileImage/profileImageHelperV2';
import { isChinaUser } from 'utils/ChinaUtil';
import { USER_ROLES } from 'constants/UserRoles';

// Custom events raised by this object
var Events = {
    VALIDATION_SUCCEEDED: 'VALIDATION_SUCCEEDED' // Raised if validation has succeeded.
};

/**
 * Specifies the various types of visibility. These should correspond to the types in the DTO
 */
var ProfileVisibility = {
    PUBLIC: 'public',
    PRIVATE: 'private',
    FOLLOWING: 'following'
};

/**
 * Specifies the types of SocialActivityTypes.  Should correspond to SocialActivityType in UserProfileService.
 */
var SocialActivityType = {
    RUNNING: 'running',
    CYCLING: 'cycling',
    SWIMMING: 'swimming',
    TRIATHLON: 'triathlon',
    DUATHLON: 'duathlon',
    CROSS_TRAINING: 'cross_training',
    WALKING: 'walking',
    HIKING: 'hiking',
    WEIGHT_TRAINING: 'weight_training',
    SNOW_SPORTS: 'snow_sports',
    WATER_SPORTS: 'water_sports',
    OTHER: 'other'
};

var instanceCount = 0;
var viewerDisplayName = window.VIEWER_SOCIAL_PROFILE != null ? window.VIEWER_SOCIAL_PROFILE.displayName : null;

var UserProfile = Backbone.Model.extend({
    backend: 'gcapi',

    url: function() {
        return '/userprofile-service/socialProfile/' + this.displayName;
    },

    initialize: function(args) {
        if (
            typeof args !== 'undefined' &&
            args !== null &&
            typeof args.displayName !== 'undefined' &&
            args.displayName !== null
        ) {
            this.displayName = args.displayName;
        } else {
            this.validationEnabled = false; // Keeps track of whether validation is enabled

            // Keep a copy of the displayName, in case it gets updated
            this.displayName = this.get('displayName');

            // We want to encourage all consumers to use the global instance of the viewer's social profile.  We keep
            // count here so that we can log additional instances.
            if (this.displayName === viewerDisplayName) {
                instanceCount++;

                if (instanceCount > 1) {
                    // Please consider using the global instance, by requiring ViewerSocialProfile.
                    console.log('!!!!Detected extra instance of UserProfile.!!!!');
                }
            }

            // Invoked when this object has been fetched sucessfully. Sets the fetched bit.
            this.bind(
                'change',
                function() {
                    this.fetched = true;
                },
                this
            );
            this.bind('sync', this.onSync, this);
        }
    },

    getDisplayName: function() {
        return this.get('displayName');
    },

    /**
     * Called when the model has been synced with the server. In case the user name has changed update it.
     */
    onSync: function() {
        this.displayName = this.get('displayName');
    },

    getUserType: function() {
        var userRoles = this.get('userRoles');

        var hasFitnessRole = _.contains(userRoles, 'ROLE_FITNESS_USER');
        var hasWellnessRole = _.contains(userRoles, 'ROLE_WELLNESS_USER');

        if (hasFitnessRole && hasWellnessRole) {
            return 'CROSSOVER_USER';
        } else if (hasFitnessRole) {
            return 'TRANSITIONED_USER';
        } else {
            return 'NEW_USER';
        }
    },

    hasUserExperience: function() {
        return (
            _.contains(this.get('userRoles'), 'ROLE_WELLNESS_USER') ||
            _.contains(this.get('userRoles'), 'ROLE_FITNESS_USER') ||
            _.contains(this.get('userRoles'), 'ROLE_OUTDOOR_USER') ||
            _.contains(this.get('userRoles'), 'ROLE_DIVE_USER') ||
            _.contains(this.get('userRoles'), 'ROLE_MARINE_USER')
        );
    },

    hasStepsEnabled: function() {
        if (typeof this.stepsEnabled === 'undefined') {
            this.stepsEnabled = this.isWellnessUser();
        }

        return this.stepsEnabled;
    },

    isAdminUser: function() {
        return _.contains(this.get('userRoles'), 'ROLE_ADMINISTRATOR');
    },

    isCustomerService: function() {
        return _.contains(this.get('userRoles'), 'ROLE_CUSTOMERSERVICE');
    },

    isCustomerServiceAdmin: function() {
        return _.contains(this.get('userRoles'), 'ROLE_CUSTOMER_SERVICE_ADMIN');
    },

    isDeviceAdmin: function() {
        return _.contains(this.get('userRoles'), 'ROLE_DEVICE_ADMIN');
    },

    getProfileImageUrlSmall: function() {
        return this.get('profileImageUrlSmall') || profileImageHelperV2.getDefaultProfileImage();
    },

    getProfileImageUrlMedium: function() {
        return this.get('profileImageUrlMedium') || profileImageHelperV2.getDefaultProfileImage();
    },

    getProfileImageUrlLarge: function() {
        return this.get('profileImageUrlLarge') || profileImageHelperV2.getDefaultProfileImage();
    },

    hasMBTesterRole: function() {
        return this.has('userRoles') ? this.get('userRoles').indexOf('ROLE_MBTESTER') !== -1 : false;
    },

    isUserLoggedIn: function() {
        return !!this.get('displayName');
    },

    isFitnessUser: function() {
        return this.hasRole('ROLE_FITNESS_USER');
    },

    isWellnessUser: function() {
        return this.hasRole('ROLE_WELLNESS_USER');
    },

    isOutdoorUser: function() {
        return this.hasRole('ROLE_OUTDOOR_USER');
    },

    isDiveUser: function() {
        return this.hasRole('ROLE_DIVE_USER');
    },

    isMarineUser: function() {
        return this.hasRole('ROLE_MARINE_USER');
    },

    isAlphaUser: function() {
        return this.hasRole('ROLE_CONNECTBETAUSER');
    },

    isUserSuspended: function() {
        return this.hasRole('ROLE_PARTIALLY_SUSPENDED');
    },

    isRaceEventProvider: function() {
        return this.hasRole('ROLE_RACE_EVENT_PROVIDER');
    },

    isGCPUser: function() {
        return this.hasRole('ROLE_SP_FEATURE_1');
    },

    isPremiumUser: function() {
        return this.hasRole('ROLE_SP_FEATURE_3');
    },

    isPremiumExclusiveBadgeUser: function() {
        return this.hasRole('ROLE_SP_FEATURE_4');
    },

    isPremiumBonusBadgePointUser: function() {
        return this.hasRole('ROLE_SP_FEATURE_4');
    },

    isManagedAccountsAdminUser: function() {
        return this.hasRole(USER_ROLES.ROLE_ADMIN_USER) && !isChinaUser();
    },

    isManagedAccountsChildUser: function() {
        return this.hasRole(USER_ROLES.ROLE_CHILD_USER) && !isChinaUser();
    },

    hasRole: function(role) {
        if (this.get('userRoles')) {
            return _.contains(this.get('userRoles'), role);
        }
    }
});

UserProfile.ProfileVisibility = ProfileVisibility;
UserProfile.SocialActivityType = SocialActivityType;
UserProfile.Events = Events;

export default UserProfile;
